'use client';

import React, { useEffect } from 'react';
import Link from 'next/link';

import { Download, ArrowRight } from 'lucide-react';
import { trackHeroSectionView, trackHeroCTAClick } from '@/lib/gaEvents';

interface LandingHeroProps {
  pageContext?: 'homepage' | 'landing_page';
}

export default function LandingHero({ pageContext = 'homepage' }: LandingHeroProps) {
  // Fire hero_section_view once on mount to measure impressions after tone corrections
  useEffect(() => {
    trackHeroSectionView({ page_context: pageContext });
  }, [pageContext]);

  return (
    <section
      className="relative min-h-screen flex flex-col justify-center overflow-hidden"
      style={{ backgroundColor: 'var(--navy)' }}
      aria-label="Hero section"
    >
      {/* Subtle warm texture overlay */}
      <div
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage:
            'radial-gradient(circle at 25% 60%, #D4891A 0%, transparent 50%), radial-gradient(circle at 75% 30%, #C0C0C0 0%, transparent 50%)',
        }}
        aria-hidden="true"
      />

      {/* Decorative amber line */}
      <div
        className="absolute top-0 left-0 right-0 h-0.5 opacity-60"
        style={{ backgroundColor: 'var(--amber)' }}
        aria-hidden="true"
      />

      <div className="relative max-w-screen-xl mx-auto px-6 lg:px-12 xl:px-20 pt-24 pb-32 flex flex-col lg:flex-row items-center gap-20 lg:gap-28">
        {/* Left: copy */}
        <div className="flex-1 text-center lg:text-left max-w-2xl mx-auto lg:mx-0">
          {/* Eyebrow */}
          <div className="inline-flex items-center gap-2 mb-8">
            <div
              className="h-px w-8"
              style={{ backgroundColor: 'var(--amber)' }}
              aria-hidden="true"
            />
            <span
              className="font-body text-lg font-600 uppercase tracking-widest text-center"
              style={{ color: 'var(--amber)', letterSpacing: '0.12em' }}
            >
              PLAIN ENGLISH AI FOR PROFESSIONALS OVER 50.
            </span>
            <div
              className="h-px w-8"
              style={{ backgroundColor: 'var(--amber)' }}
              aria-hidden="true"
            />
          </div>

          {/* Headline */}
          <h1
            className="font-display font-semibold leading-tight mb-8"
            style={{
              fontSize: 'clamp(2.8rem, 5.5vw, 4.25rem)',
              color: 'white',
              lineHeight: '1.12',
              letterSpacing: '-0.01em',
            }}
          >
            AI Training for Professionals Over 50
          </h1>

          {/* Subheading */}
          <p
            className="font-body text-lg lg:text-xl mb-6 max-w-xl mx-auto lg:mx-0"
            style={{ color: 'rgba(255,255,255,0.72)', lineHeight: '1.8' }}
          >
            The plain English guide for professionals over 50. Starting with the tools you already have.
          </p>

          {/* Broader AI positioning line */}
          <p
            className="font-body text-sm mb-12 max-w-xl mx-auto lg:mx-0 text-center lg:text-left"
            style={{ color: 'rgba(255,255,255,0.50)', lineHeight: '1.8' }}
          >
            We start with confidence. Then build practical AI skills around it.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
            <Link
              href="/free-lesson-screen"
              onClick={() => trackHeroCTAClick({ cta_label: 'start_free', page_context: pageContext })}
              className="group inline-flex items-center justify-center gap-2.5 font-body font-semibold text-base px-8 py-4 rounded-xl transition-all duration-200 active:scale-95"
              style={{ backgroundColor: 'var(--amber)', color: 'white' }}
            >
              Start Free — No Credit Card
              <ArrowRight
                size={18}
                className="transition-transform duration-200 group-hover:translate-x-1"
              />
            </Link>

            <a
              href="/assets/images/ThatClicked_AICheatSheet.pdf"
              download="ThatClicked_AICheatSheet.pdf"
              onClick={() => trackHeroCTAClick({ cta_label: 'download_cheat_sheet', page_context: pageContext })}
              className="group inline-flex items-center justify-center gap-2.5 font-body font-medium text-base px-8 py-4 rounded-xl transition-all duration-200 active:scale-95 border"
              style={{
                borderColor: 'rgba(255,255,255,0.25)',
                color: 'rgba(255,255,255,0.85)',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.borderColor = 'rgba(255,255,255,0.5)';
                (e.currentTarget as HTMLAnchorElement).style.color = 'white';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.borderColor = 'rgba(255,255,255,0.25)';
                (e.currentTarget as HTMLAnchorElement).style.color = 'rgba(255,255,255,0.85)';
              }}
            >
              <Download size={17} />
              Download Free AI Cheat Sheet
            </a>
          </div>

          {/* Trust signal */}
          <div className="mt-12 flex items-center gap-6 justify-center lg:justify-start">
            <div className="flex -space-x-2 items-center">
              {/* Anna's photo — first, slightly larger, with amber ring */}
              <div
                className="relative w-10 h-10 rounded-full flex-shrink-0 overflow-hidden"
                style={{
                  boxShadow: '0 0 0 2px var(--amber)',
                  zIndex: 5,
                }}
                aria-hidden="true"
              >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src="/assets/images/IMG_0355-1784420498663.PNG"
                  alt="Anna Rippon, founder of ThatClicked"
                  className="w-full h-full object-cover rounded-full"
                  onError={(e) => {
                    const target = e.currentTarget as HTMLImageElement;
                    target.style.display = 'none';
                    const parent = target.parentElement;
                    if (parent) {
                      parent.style.backgroundColor = 'var(--amber)';
                      parent.style.display = 'flex';
                      parent.style.alignItems = 'center';
                      parent.style.justifyContent = 'center';
                      const span = document.createElement('span');
                      span.className = 'text-xs font-bold text-white';
                      span.textContent = 'A';
                      parent.appendChild(span);
                    }
                  }}
                />
              </div>
              {/* Four member avatars */}
              {['bg-amber-300', 'bg-blue-300', 'bg-green-300', 'bg-purple-300'].map((bg, i) => (
                <div
                  key={`avatar-${i}`}
                  className={`w-8 h-8 rounded-full border-2 border-navy-500 ${bg} flex items-center justify-center`}
                  style={{ zIndex: 4 - i }}
                  aria-hidden="true"
                >
                  <span className="text-xs font-semibold text-navy-700">
                    {['M', 'P', 'J', 'S'][i]}
                  </span>
                </div>
              ))}
            </div>
            <p
              className="font-body text-sm"
              style={{ color: 'rgba(255,255,255,0.55)' }}
            >
              Created by Anna Rippon. No technical experience needed — everything explained in plain English.
            </p>
          </div>
        </div>

        {/* Right: prompt mockup */}
        <div className="flex-shrink-0 w-full max-w-sm lg:max-w-md">
          <div
            className="rounded-2xl overflow-hidden shadow-warm-lg"
            style={{ border: '1px solid rgba(255,255,255,0.1)' }}
            aria-label="Example of a Copilot prompt in action"
            role="img"
          >
            {/* Prompt window title bar */}
            <div
              className="px-4 py-3 flex items-center gap-2"
              style={{ backgroundColor: 'rgba(255,255,255,0.07)' }}
            >
              {[
                { bg: '#3D5080' },
                { bg: '#7A8FAD' },
                { bg: '#C0C0C0' },
              ].map((dot, i) => (
                <div
                  key={`dot-${i}`}
                  className="w-2.5 h-2.5 rounded-full"
                  style={{ backgroundColor: dot.bg }}
                  aria-hidden="true"
                />
              ))}
              <span
                className="ml-2 font-body text-xs"
                style={{ color: 'rgba(255,255,255,0.4)' }}
              >
                AI at work
              </span>
            </div>

            {/* Prompt content */}
            <div
              className="p-6"
              style={{ backgroundColor: 'rgba(255,255,255,0.04)' }}
            >
              <p
                className="font-body text-xs uppercase tracking-widest mb-3"
                style={{ color: 'var(--amber)', letterSpacing: '0.1em' }}
              >
                Your prompt
              </p>
              <p
                className="font-body text-sm leading-relaxed mb-6"
                style={{ color: 'rgba(255,255,255,0.85)' }}
              >
                &ldquo;Summarise this email thread and tell me what I need to decide before the end of the week.&rdquo;
              </p>

              <div
                className="rounded-lg p-4"
                style={{ backgroundColor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.1)' }}
              >
                <p
                  className="font-body text-xs uppercase tracking-widest mb-2"
                  style={{ color: 'var(--silver)', letterSpacing: '0.1em' }}
                >
                  Copilot
                </p>
                <p
                  className="font-body text-sm leading-relaxed"
                  style={{ color: 'rgba(255,255,255,0.7)' }}
                >
                  Here&rsquo;s a summary of the thread. There are two decisions you need to make by Friday: the supplier contract renewal and the Q2 budget sign-off...
                </p>
                <span
                  className="inline-block w-0.5 h-4 mt-1 align-middle"
                  style={{ backgroundColor: 'var(--amber)' }}
                  aria-hidden="true"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}